import { useCallback, useLayoutEffect, useMemo } from 'react';
import { AppText, Box, Row } from '@streem/ui-react';
import { streem } from 'streem-sdk-protobuf';
import { useFormikContext } from 'formik';
import {
    Disposition,
    RefundReason,
    OutOfScopeReason,
    RoomOutcomeFormPayload,
} from '../../types/disposition.types';
import { LabelSearchInput } from './call_diagnosis';
import { useGlobalStore } from '../../hooks/use_global_context';
import { BlueCheckBox } from './room_outcome_form_styles';
import { RefundSection } from './refund_section';
import { OutOfScopeDiscussion } from './out_of_scope_discussion';
import { getInitialValue } from '../../util/call_disposition_helpers';
import { useCallSourceData } from '../../hooks/use_call_source_data';
import { useWillOfferCustomField } from '../../hooks/use_will_offer_custom_field';

interface DispositionDetailSelectionProps {
    formValues: {
        disposition: Disposition | undefined;
        shouldRefund?: boolean;
        refundReason?: RefundReason;
        contractor?: boolean;
        training?: boolean;
        isOutOfScopeDiscussion?: boolean;
        screenShare?: boolean;
        hvacMentioned?: boolean;
    };
    dispositionOptions: streem.api.CompanyRoomOutcomeConfig.ISection;
    customFieldOptions?: {
        refund?: streem.api.CompanyRoomOutcomeConfig.ISection;
        training?: streem.api.CompanyRoomOutcomeConfig.ISection;
        contractor?: streem.api.CompanyRoomOutcomeConfig.ISection;
        outOfScope?: streem.api.CompanyRoomOutcomeConfig.ISection;
        screenShare?: streem.api.CompanyRoomOutcomeConfig.ISection;
        hvacMentioned?: streem.api.CompanyRoomOutcomeConfig.ISection;
    };
    outOfScopeReasons?: OutOfScopeReason[];
    setOutOfScopeReasons: React.Dispatch<React.SetStateAction<OutOfScopeReason[]>>;
}

export const DROPPED_RECONNECT = 'dropped - reconnect';

export const DispositionDetailSection = ({
    formValues,
    dispositionOptions,
    customFieldOptions,
    outOfScopeReasons,
    setOutOfScopeReasons,
}: DispositionDetailSelectionProps): JSX.Element => {
    const {
        disposition,
        shouldRefund = undefined,
        refundReason = undefined,
        contractor = undefined,
        training = undefined,
        isOutOfScopeDiscussion = undefined,
        screenShare = undefined,
        hvacMentioned = undefined,
    } = formValues;
    const { companySettingsStore } = useGlobalStore();
    const { callSource } = useCallSourceData();
    const { setFieldValue, values } = useFormikContext<RoomOutcomeFormPayload>();
    const shouldShowRefundBox = useWillOfferCustomField(customFieldOptions?.refund);

    const callSourceCode = callSource?.code.toLocaleLowerCase();

    const filterByCallSourceCode = useCallback(
        (callSourceCodes: string[] | null, roomCallSourceCode: string) => {
            if (callSourceCode) {
                return callSourceCodes?.includes(roomCallSourceCode);
            } else {
                return true;
            }
        },
        [callSourceCode],
    );

    const filteredOptions = useMemo(() => {
        return dispositionOptions.options
            .filter(option => {
                return filterByCallSourceCode(option.entry.callSourceCodes, callSourceCode);
            })
            .map(option => {
                return {
                    value: option.entry.code,
                    label: option.entry.label,
                };
            });
    }, [callSourceCode, dispositionOptions.options, filterByCallSourceCode]);

    const handleToggleContractorCall = () => {
        const { customFields } = values;
        if (customFields?.contractor === undefined) {
            return;
        }
        const { contractor } = customFields;
        setFieldValue('customFields', {
            ...customFields,
            contractor: !contractor,
        });
    };

    const handleToggleScreenShareCall = () => {
        const { customFields } = values;
        if (customFields?.screenShare === undefined) {
            return;
        }
        const { screenShare } = customFields;
        setFieldValue('customFields', {
            ...customFields,
            screenShare: !screenShare,
        });
    };

    const handleToggleTrainingCall = () => {
        const { customFields } = values;
        if (customFields?.training === undefined) {
            return;
        }
        const { training } = customFields;
        setFieldValue('customFields', {
            ...customFields,
            training: !training,
        });
    };

    const handleToggleHvacMentionedCall = () => {
        const { customFields } = values;
        if (customFields?.hvacMentioned === undefined) {
            return;
        }
        const { hvacMentioned } = customFields;
        setFieldValue('customFields', {
            ...customFields,
            hvacMentioned: !hvacMentioned,
        });
    };

    const handleToggleShouldRefund = () => {
        const { customFields } = values;
        if (!customFields?.refund) {
            return;
        }
        const { refund } = customFields;

        if (customFields.refund.shouldRefund) {
            setFieldValue('customFields', {
                ...customFields,
                refund: {
                    shouldRefund: !refund.shouldRefund,
                    reason: refund.reason,
                },
            });
            return;
        }
        setFieldValue('customFields', {
            ...customFields,
            refund: {
                ...customFields.refund,
                shouldRefund: !refund.shouldRefund,
            },
        });
    };

    const updateRequestRefundCheckbox = (dispositionCode: string, prevDispositionCode: string) => {
        if (!shouldRefund && dispositionCode === DROPPED_RECONNECT) {
            // Check
            handleToggleShouldRefund();
        }
        if (prevDispositionCode === DROPPED_RECONNECT && shouldRefund) {
            // Uncheck
            handleToggleShouldRefund();
        }
    };

    const handleDispositionChange = (val: { value: string; label: string }) => {
        const newDisposition = {
            code: val.value,
            label: val.label,
        };

        if (shouldShowRefundBox) {
            updateRequestRefundCheckbox(newDisposition.code, disposition.code);
        }
        setFieldValue('disposition', newDisposition);
    };

    const initialRefundReason = useMemo(() => {
        if (shouldRefund && refundReason) {
            return getInitialValue<RefundReason>(
                refundReason,
                customFieldOptions.refund?.options.map(option => ({
                    code: option.entry.code,
                    label: option.entry.label,
                })),
            );
        } else {
            return { value: '', label: '' };
        }
        // eslint-disable-next-line react-hooks/rules-of-hooks,react-hooks/exhaustive-deps
    }, [shouldRefund, refundReason]);

    useLayoutEffect(() => {
        if (shouldRefund) {
            const refundReasonSearchInput = document.querySelector(
                '[aria-label="refund reason options"]',
            ) as HTMLInputElement;
            refundReasonSearchInput.focus();
        } else {
            const dispositionSearchInput = document.querySelector(
                '[aria-label="call disposition options"]',
            ) as HTMLInputElement;
            dispositionSearchInput.focus();
        }
    }, [shouldRefund]);

    return (
        <Box width="100%">
            <LabelSearchInput
                ariaLabel="call disposition options"
                label="Call Disposition"
                placeholder="Search here"
                handleChange={handleDispositionChange}
                options={filteredOptions}
                initialValue={getInitialValue<Disposition>(
                    disposition,
                    dispositionOptions.options.map(option => ({
                        code: option.entry.code,
                        label: option.entry.label,
                    })),
                )}
            />

            {shouldShowRefundBox && (
                <RefundSection
                    shouldRefund={shouldRefund}
                    handleToggleShouldRefund={handleToggleShouldRefund}
                    customFieldOptions={customFieldOptions}
                    initialRefundReason={initialRefundReason}
                />
            )}

            {companySettingsStore.ahsVirtualExpertEnabled && (
                <OutOfScopeDiscussion
                    displayOutOfScopeReasons={isOutOfScopeDiscussion}
                    outOfScopeReasons={outOfScopeReasons}
                    customFieldOptions={customFieldOptions}
                    setOutOfScopeReasons={setOutOfScopeReasons}
                />
            )}

            <AppText size="medium" headingFontFamily bold as={'p'} style={{ paddingTop: '2.5rem' }}>
                Other Selections
            </AppText>

            {customFieldOptions?.screenShare &&
                filterByCallSourceCode(
                    customFieldOptions?.screenShare?.callSourceCodes,
                    callSourceCode,
                ) && (
                    <Row>
                        <BlueCheckBox
                            id="used screen share"
                            dataTestId="screen-share-checkbox"
                            description="Used Screen Share"
                            checked={screenShare}
                            handleChange={handleToggleScreenShareCall}
                        />
                    </Row>
                )}

            {customFieldOptions?.contractor &&
                filterByCallSourceCode(
                    customFieldOptions?.contractor?.callSourceCodes,
                    callSourceCode,
                ) && (
                    <Row>
                        <BlueCheckBox
                            id="contractor call"
                            dataTestId="contractor-call-checkbox"
                            description="Contractor Video Chat"
                            checked={contractor}
                            handleChange={handleToggleContractorCall}
                        />
                    </Row>
                )}

            {customFieldOptions?.training &&
                filterByCallSourceCode(
                    customFieldOptions?.training?.callSourceCodes,
                    callSourceCode,
                ) && (
                    <Row>
                        <BlueCheckBox
                            id="training call"
                            dataTestId="training-call-checkbox"
                            description="Use This Call For Training Sessions"
                            checked={training}
                            handleChange={handleToggleTrainingCall}
                        />
                    </Row>
                )}
            {customFieldOptions?.hvacMentioned &&
                filterByCallSourceCode(
                    customFieldOptions?.hvacMentioned?.callSourceCodes,
                    callSourceCode,
                ) && (
                    <Row>
                        <BlueCheckBox
                            id="hvac mentioned"
                            dataTestId="hvac-mentioned-checkbox"
                            description="HVAC Upgrade Mentioned"
                            checked={hvacMentioned}
                            handleChange={handleToggleHvacMentionedCall}
                        />
                    </Row>
                )}
        </Box>
    );
};
